import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar";
import Header from "../Header";
import GovernorPanel from "../components/GovernorPanel";
import "./WhatsApp.css";
import axios from "axios";
import API_BASE_URL from "../../config";
import {
  BsWhatsapp,
  BsPlusLg,
  BsTrash3Fill,
  BsCheckCircleFill,
  BsXCircleFill,
  BsXLg,
  BsExclamationTriangleFill,
  BsArrowRepeat,
  BsBagPlus,
  BsGearFill,
  BsPencil,
} from "react-icons/bs";

const getToken = () => localStorage.getItem("userToken");

export default function WhatsApp() {
  const navigate = useNavigate();
  const headers = useCallback(() => {
    const t = getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notSubscribed, setNotSubscribed] = useState(false);
  const [toast, setToast] = useState(null);

  // link flow
  const [linking, setLinking] = useState(false);
  const [linkId, setLinkId] = useState(null);
  const [qr, setQr] = useState(null);
  const [linkStatus, setLinkStatus] = useState("connecting");
  const [linkLoading, setLinkLoading] = useState(null);
  const qrPoll = useRef(null);

  const [numberToRemove, setNumberToRemove] = useState(null);
  const [governorFor, setGovernorFor] = useState(null);
  const [renameFor, setRenameFor] = useState(null); // the number object being renamed
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) window.location.href = "/login";
  }, []);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchNumbers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/whatsapp/numbers`, { headers: headers(), timeout: 15000 });
      setNumbers(res.data.numbers || []);
      setNotSubscribed(false);
    } catch (err) {
      if (err.response?.status === 402) setNotSubscribed(true);
      else console.error("WhatsApp numbers error:", err.message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchNumbers(); }, [fetchNumbers]);

  const startLink = async () => {
    setLinking(true);
    setQr(null);
    setLinkStatus("connecting");
    setLinkId(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/whatsapp/numbers`, {}, { headers: headers(), timeout: 20000 });
      setLinkId(res.data.id);
    } catch (err) {
      if (err.response?.status === 402) { setNotSubscribed(true); setLinking(false); }
      else setLinkStatus("error");
    }
  };

  useEffect(() => {
    if (!linkId || !linking) return;
    let cancelled = false;
    // Poll fast (900ms) while we're waiting for the QR to appear, so it shows the
    // instant the engine emits it; ease off to 2.5s once the QR is on screen and
    // we're just waiting for the user to scan.
    const poll = async () => {
      let hasQr = false;
      try {
        const res = await axios.get(`${API_BASE_URL}/whatsapp/numbers/${linkId}/qr`, { headers: headers(), timeout: 15000 });
        if (cancelled) return;
        hasQr = !!res.data.qr;
        setQr(res.data.qr || null);
        setLinkStatus(res.data.status);
        setLinkLoading(res.data.loading || null);
        if (res.data.status === "connected") {
          showToast("success", "WhatsApp number connected!");
          setTimeout(() => { if (!cancelled) { setLinking(false); fetchNumbers(); } }, 1200);
          return;
        }
      } catch (err) { /* keep polling */ }
      if (!cancelled) qrPoll.current = setTimeout(poll, hasQr ? 2500 : 900);
    };
    poll();
    return () => { cancelled = true; clearTimeout(qrPoll.current); };
  }, [linkId, linking, headers, fetchNumbers]);

  const closeLink = () => { clearTimeout(qrPoll.current); setLinking(false); fetchNumbers(); };

  // Re-open the QR modal for an existing (not-connected) number from its card,
  // and trigger a fresh QR so the user can scan again.
  const reconnect = (number) => {
    setLinkId(number.id);
    setQr(null);
    setLinkStatus("connecting");
    setLinkLoading(null);
    setLinking(true);
    axios
      .post(`${API_BASE_URL}/whatsapp/numbers/${number.id}/relink`, {}, { headers: headers(), timeout: 20000 })
      .catch(() => { /* poll will still pick up the QR */ });
  };

  // Re-trigger a fresh QR if the scan failed / the code expired.
  const refreshQr = async () => {
    if (!linkId) return;
    setQr(null); setLinkStatus("connecting"); setLinkLoading(null);
    try {
      await axios.post(`${API_BASE_URL}/whatsapp/numbers/${linkId}/relink`, {}, { headers: headers(), timeout: 20000 });
    } catch (err) {
      showToast("error", "Couldn't refresh the QR. Try again.");
    }
  };

  const removeNumber = async () => {
    if (!numberToRemove) return;
    try {
      await axios.delete(`${API_BASE_URL}/whatsapp/numbers/${numberToRemove}`, { headers: headers(), timeout: 15000 });
      showToast("success", "Number removed.");
      fetchNumbers();
    } catch (err) {
      showToast("error", "Failed to remove number.");
    }
    setNumberToRemove(null);
  };

  const openRename = (n) => {
    setRenameFor(n);
    setRenameValue(n.label || "");
  };

  const saveRename = async () => {
    if (!renameFor) return;
    setRenameSaving(true);
    try {
      await axios.patch(`${API_BASE_URL}/whatsapp/numbers/${renameFor.id}`,
        { label: renameValue.trim() }, { headers: headers(), timeout: 15000 });
      showToast("success", "Name updated.");
      setRenameFor(null);
      fetchNumbers();
    } catch (err) {
      showToast("error", "Couldn't update the name.");
    } finally {
      setRenameSaving(false);
    }
  };

  const statusInfo = (s) => {
    if (s === "connected") return { label: "Connected", cls: "wa-st-online" };
    if (s === "disconnected" || s === "logged_out") return { label: "Disconnected", cls: "wa-st-offline" };
    return { label: "Connecting…", cls: "wa-st-pending" };
  };

  return (
    <div className="page-container">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="container mt-4">
          <div className="page-header dash-head">
            <div>
              <h4>WhatsApp numbers</h4>
              <p className="page-subtitle">Connect business numbers to send &amp; receive on WhatsApp.</p>
            </div>
            {!notSubscribed && numbers.length > 0 && (
              <button className="dash-cta" onClick={startLink}>＋ Add number</button>
            )}
          </div>

          {notSubscribed ? (
            <div className="wa-empty">
              <BsWhatsapp size={48} />
              <h5>WhatsApp isn’t active on your account</h5>
              <p>Subscribe to the WhatsApp service to connect a number.</p>
              <button className="wa-add-btn" onClick={() => navigate("/services")}><BsBagPlus /> View WhatsApp plans</button>
            </div>
          ) : loading ? (
            <div className="page-loading"><div className="spinner" /><p>Loading numbers…</p></div>
          ) : numbers.length === 0 ? (
            <div className="wa-empty">
              <BsWhatsapp size={48} />
              <h5>No WhatsApp numbers yet</h5>
              <p>Connect your first number by scanning a QR code with WhatsApp on your phone.</p>
              <button className="wa-add-btn" onClick={startLink}><BsPlusLg /> Add Number</button>
            </div>
          ) : (
            <div className="wa-tblcard">
              <div className="wa-tbl-h"><h6>Numbers</h6></div>
              <div className="table-responsive">
                <table className="wa-table">
                  <thead><tr><th>Number</th><th>Status</th><th>Today</th><th></th></tr></thead>
                  <tbody>
                    {numbers.map((n) => {
                      const st = statusInfo(n.status);
                      const tag = st.cls === "wa-st-online" ? "ok" : st.cls === "wa-st-pending" ? "pend" : "off";
                      return (
                        <tr key={n.id}>
                          <td>
                            <div className="wa-nm">{n.label || "WhatsApp Number"}</div>
                            <div className="wa-sub2">{n.phone || "Pending…"}</div>
                          </td>
                          <td><span className={`wa-tag ${tag}`}>{st.label}</span></td>
                          <td>{n.messages_sent_today || 0}</td>
                          <td className="wa-actions">
                            <button className="wa-ic" title="Rename" onClick={() => openRename(n)}><BsPencil /></button>
                            {n.status !== "connected" && (
                              <button className="wa-ic" title="Show QR / Reconnect" onClick={() => reconnect(n)}><BsArrowRepeat /></button>
                            )}
                            {n.status === "connected" && (
                              <button className="wa-ic" title="Sending limits" onClick={() => setGovernorFor(n.id)}><BsGearFill /></button>
                            )}
                            <button className="wa-ic danger" title="Remove number" onClick={() => setNumberToRemove(n.id)}><BsTrash3Fill /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Link / QR modal */}
      {linking && (
        <div className="modal-overlay" onClick={closeLink}>
          <div className="wa-link-modal" onClick={(e) => e.stopPropagation()}>
            <button className="wa-modal-close" onClick={closeLink} aria-label="Close"><BsXLg /></button>
            <div className="wa-modal-head">
              <div className="wa-icon-wrap wa-icon-lg"><BsWhatsapp /></div>
              <h5>Connect a WhatsApp number</h5>
            </div>

            {linkStatus === "connected" ? (
              <div className="wa-connected"><BsCheckCircleFill /><span>Connected!</span></div>
            ) : linkStatus === "syncing" ? (
              <div className="wa-syncing">
                <BsCheckCircleFill className="wa-syncing-check" />
                <h6>Scanned successfully!</h6>
                <BsArrowRepeat className="wa-spin wa-syncing-spin" />
                <p>Setting up your session{linkLoading ? ` — ${linkLoading}%` : ""}…<br />this can take up to a minute. Keep this open.</p>
              </div>
            ) : (linkStatus === "logged_out" || linkStatus === "disconnected" || linkStatus === "error") ? (
              <div className="wa-syncing">
                <BsXCircleFill className="wa-syncing-fail" />
                <h6>The scan didn’t complete</h6>
                <p>The code may have expired or the link was declined.</p>
                <button className="wa-add-btn" onClick={refreshQr}><BsArrowRepeat /> Show a new QR</button>
              </div>
            ) : (
              <div className="wa-qr-area">
                <div className="wa-qr">
                  {qr ? (
                    <img src={qr} alt="WhatsApp QR" />
                  ) : (
                    <div className="wa-qr-loading"><BsArrowRepeat className="wa-spin" /><span>Generating QR…</span></div>
                  )}
                </div>
                <div className="wa-qr-side">
                  <ol className="wa-steps">
                    <li>Open <strong>WhatsApp</strong> on your phone</li>
                    <li>Tap <strong>Settings → Linked devices</strong></li>
                    <li>Tap <strong>Link a device</strong></li>
                    <li>Point your phone at this screen to scan</li>
                  </ol>
                  <div className="wa-waiting"><BsArrowRepeat className="wa-spin" /> Waiting for scan…</div>
                  <button className="wa-refresh-qr" onClick={refreshQr}><BsArrowRepeat /> QR not working? Refresh</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Remove confirm */}
      {numberToRemove && (
        <div className="modal-overlay" onClick={() => setNumberToRemove(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon-wrap modal-icon-danger"><BsExclamationTriangleFill /></div>
            <h5>Remove WhatsApp number?</h5>
            <p>This disconnects the number and removes it from your account. You’ll need to re-scan to reconnect.</p>
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => setNumberToRemove(null)}>Cancel</button>
              <button className="btn-modal-danger" onClick={removeNumber}>Remove</button>
            </div>
          </div>
        </div>
      )}

      {/* Rename number */}
      {renameFor && (
        <div className="modal-overlay" onClick={() => setRenameFor(null)}>
          <div className="wa-rename-modal" onClick={(e) => e.stopPropagation()}>
            <button className="wa-modal-close" onClick={() => setRenameFor(null)} aria-label="Close"><BsXLg /></button>
            <div className="wa-gov-head"><BsPencil /><h5>Name this number</h5></div>
            <p className="wa-rename-sub">{renameFor.phone || "Not connected yet"}</p>
            <input
              className="wa-rename-input"
              type="text"
              maxLength={60}
              placeholder="e.g. Support line, Sales AU"
              value={renameValue}
              autoFocus
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveRename(); }}
            />
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => setRenameFor(null)}>Cancel</button>
              <button className="wa-rename-save" disabled={renameSaving} onClick={saveRename}>
                {renameSaving ? "Saving…" : "Save name"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sending limits (governor) */}
      {governorFor && (
        <div className="modal-overlay" onClick={() => setGovernorFor(null)}>
          <div className="wa-gov-modal" onClick={(e) => e.stopPropagation()}>
            <button className="wa-modal-close" onClick={() => setGovernorFor(null)} aria-label="Close"><BsXLg /></button>
            <div className="wa-gov-head"><BsGearFill /><h5>Sending limits</h5></div>
            <GovernorPanel channel="whatsapp" ownerId={governorFor} />
          </div>
        </div>
      )}

      {toast && (
        <div className={`toast-notification ${toast.type === "success" ? "toast-success" : "toast-error"}`}>
          {toast.type === "success" ? <BsCheckCircleFill /> : <BsXCircleFill />}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}
